import { NgClass, NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { AuthService } from '../../Auth/auth.service';
import { Router, RouterLink } from '@angular/router';
import { FormSubmitService } from '../../Services/Form Data/formsubmit.service';
import { DataService } from '../../Services/Data Service/data.service';
import { FormDataService } from '../../Services/Form Data/form-data.service';
import { concatMap, from, switchMap, tap } from 'rxjs';
import { describeHttpError, formatEmailDeliveryUserMessage, NotificationService } from '../../Services/Data Service/notification.service';
import { isAdminRole } from '../../roles';

@Component({
  selector: 'app-signup-or-login',
  standalone: true,
  imports: [FormsModule, NgIf, NgClass, RouterLink],
  templateUrl: './signup-or-login.component.html',
  styleUrl: './signup-or-login.component.css'
})
export class SignupOrLoginComponent implements OnInit {
  readonly tutorialVideoUrl = 'https://www.youtube.com/embed/0bRdzzMJY6c?si=id8cY9oCRwkQc1VO';
  isLightboxOpen = false;
  selectedVideoUrl: SafeResourceUrl = '';

  openLightbox(url: string): void {
    const hasQuery = url.includes('?');
    const params = [
      'autoplay=1',
      'enablejsapi=1',
      'origin=' + (typeof window !== 'undefined' ? window.location.origin : ''),
      'rel=0',
      'modestbranding=1'
    ];
    const paramString = params.join('&');
    const finalUrl = hasQuery ? url + '&' + paramString : url + '?' + paramString;
    this.selectedVideoUrl = this.sanitizer.bypassSecurityTrustResourceUrl(finalUrl);
    this.isLightboxOpen = true;
  }

  closeLightbox(): void {
    this.isLightboxOpen = false;
    this.selectedVideoUrl = '';
  }

  error_message = '';
  user: any = '';

  email = '';
  password = '';
  createPassword = '';
  confirmPassword = '';
  emailChecked = false;
  isNewUser = false;
  accountStatus = '';
  statusMessage: string = 'Please enter your email to continue.';

  loading = false;
  hidePassword = true;
  submissionsRestricted = false;
  submissionsSettingsLoaded = false;

  // Password validation checklist
  passwordRequirements = {
    hasUppercase: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasMinLength: false
  };

  constructor(
    private authService: AuthService,
    private sanitizer: DomSanitizer, 
    private formSubmitService: FormSubmitService,
    private formDataService: FormDataService,
    private dataService: DataService,
    private router: Router,
    private notificationService: NotificationService
  ) {}

  ngOnInit(): void {
    this.dataService.getSubmissionsSettings().subscribe({
      next: (settings) => {
        this.submissionsRestricted = settings.submissionsRestricted;
        this.submissionsSettingsLoaded = true;
      },
      error: () => {
        this.submissionsSettingsLoaded = true;
      },
    });
  }

  resetEmailCheck() {
    this.emailChecked = false;
    this.isNewUser = false; // Optional, if you want to reset both paths
    this.password = '';
    this.createPassword = '';
    this.confirmPassword = '';
  }
    
  checkEmail() { 
    if (this.submissionsRestricted) {
      return;
    }
    this.loading = true;

    if (!this.validateEmail(this.email)) {
      this.loading = false;
      return; // Exit early if invalid
    }
    this.authService.checkEmail(this.email).subscribe(
      (res) => {
        this.loading = false;
        const user = res.user;
        if (user) {
          this.accountStatus = user.strAccountStatus;
          this.emailChecked = true;
          this.isNewUser = false;
          this.statusMessage = 'Welcome back. Please enter your password to resume or review your submission.';
        } else {
          this.emailChecked = true;
          this.isNewUser = true;
          this.statusMessage = `We couldn't find an account with that email. To continue, create a password to set up your new account. \nPlease double-check your email — it cannot be edited after signup.`;
        }
      },
      (err) => {
        this.loading = false;
        console.error('Email check failed:', err);
        this.statusMessage = 'Something went wrong. Please try again.';
      }
    );
  }
  
  
  
  login() { //use same login seen on /login
      this.loading = true;
      this.dataService.setCurrentUser(null);
      this.authService.logout();

      // this.error_message = '';

      this.authService.login(this.email, this.password).subscribe(
        (response) => {
          console.log('Raw login response:', response);
          
          if (response && response.accessToken && response.refreshToken) {
            this.loading = false;
            console.log('JWT token received:', response.accessToken);
            console.log('Refresh token received:', response.refreshToken);
            this.authService.storeCredentials(this.email, response.accessToken, response.refreshToken);
            this.authService.setAccessToken(response.accessToken);
  
            this.dataService.getCurrentUser().subscribe({
              next: (user) => {
                if (!user) {
                  console.error('User not found after login');
                  return;
                }
  
                console.log('User found:', user);

                localStorage.setItem('role', user.role);
                
                if (localStorage.getItem('firstVisit') === 'true') {
                  this.sendEmailNotification(this.formDataService.getCurrCompanyID(), 0);
                }

                // Navigate based on role
                if (isAdminRole(user.role)) {
                  this.router.navigate(['/dashboard']);
                } else if (user.role === 'Vendor') {
                  this.router.navigate(['/registrationform']);
                } else if (user.role === 'Guest') {
                  this.router.navigate(['/start']);
                } else {
                  console.warn('Unknown role:', user.role);
                }          
              },
            });
          } else {
            this.loading = false;
            console.warn('Unexpected response structure:', response);
            this.error_message = 'Unexpected response from server.';
          }
        },
        (error) => {
          this.loading = false;
          // Login failures typically come back as 400/401 (invalid credentials).
          if (error.status === 400 || error.status === 401) {
            this.error_message = 'Invalid username or password.';
          } else if (error.status === 403) {
            this.error_message = 'Your session has expired. Please sign in again.';
          } else if (error.status === 500) {
            this.error_message = 'Something went wrong on the server. Please try again.';
          } else {
            this.error_message = 'Server error. Please try again later.';
          }
          console.error('Login error details:', error);
        }
      );
    }


  signup() {
    if (this.submissionsRestricted) {
      return;
    }
    this.loading = true;
  
    const validationError = this.validatePassword(this.createPassword, this.confirmPassword);
    if (validationError) {
      this.error_message = validationError;
      this.loading = false;
      return;
    }
  
    this.authService.checkEmail(this.email).subscribe({
      next: (res) => {
        if (res.user) {
          this.error_message = `We couldn't find your account details. Please <a href="/">LOG IN</a> to continue.`;
          this.loading = false;
          return;
        }
  
        // Proceed with signup if email doesn't exist
        const user = this.formDataService.getUserData();
        user.dteCreated = this.dataService.getLocalDate();
        user.role = 'Guest';
        user.strAccountStatus = 'In Progress';
        user.strAccountStatusDate = this.dataService.getLocalDate();
        user.strJobTitle = '';
        user.strContact = '';
        user.strEmail = this.email;
        user.strName = '';
        user.strPassword = this.createPassword;
        user.signatureFile = null;
  
        const steps = [
          () => this.formSubmitService.submitCompany().pipe(
            tap((response: any) => {
              const companyId = response.companyId;
              console.log('Company created:', companyId);
              this.formDataService.setCurrCompanyID(companyId);
              // this.formDataService.getUserData().company.intCompanyPK = companyId;
            })
          ),
          () => this.formSubmitService.submitUser(this.formDataService.getCurrCompanyID()),
          //create empty records for other tables; this creates smoother flow
            () => this.formSubmitService.submitBusinessTypes(this.formDataService.getCurrCompanyID()), 
            () => this.formSubmitService.submitCompanyContracts(this.formDataService.getCurrCompanyID()), 
            () => this.formSubmitService.submitContractDetails(this.formDataService.getCurrCompanyID()),
            () => this.formSubmitService.submitDirector(this.formDataService.getCurrCompanyID()), 
            () => this.formSubmitService.submitDocuments(this.formDataService.getCurrCompanyID()),
            () => this.formSubmitService.submitHSE(this.formDataService.getCurrCompanyID()),
            () => this.formSubmitService.submitSignatories(this.formDataService.getCurrCompanyID()), 
            () => this.formSubmitService.submitSubcontracts(this.formDataService.getCurrCompanyID()),
            () => this.formSubmitService.submitSubsidaries(this.formDataService.getCurrCompanyID()),
        ];
  
        from(steps).pipe(
          concatMap((step) => step())
        ).subscribe({
          next: () => console.log('Step completed'),
          error: (err) => {
            console.error('Signup failed:', err);
            this.error_message = 'Something went wrong. Please refresh and try again.';

          //UNFINISHED: perform rollback to prevent duplicate records
            // const companyId = this.formDataService.getCurrCompanyID();

            // if (companyId) {
            //   this.formSubmitService.rollbackCompany(companyId).subscribe({
            //     next: () => console.log('Rollback successful'),
            //     error: (rollbackErr) => console.error('Rollback failed:', rollbackErr)
            //   });
            // }
            this.loading = false;
          },
          complete: async () => {
            localStorage.setItem('firstVisit', 'true');
            this.loading = false;
            this.password = this.createPassword;
            this.login(); 
          }
        });
      },
      error: (err) => {
        console.error('Email check failed:', err);
        this.error_message = 'Could not validate email. Please try again.';
        this.loading = false;
      }
    });
  }
  

  validatePassword(password: string, confirmPassword: string): string | null {
    if (!password) return "Password is required.";
  
    if (!/[A-Z]/.test(password)) {
      return "Password must contain at least one uppercase letter.";
    }
  
    if (!/\d/.test(password)) {
      return "Password must contain at least one number.";
    }
  
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      return "Password must contain at least one special character.";
    }
  
    if (password.length < 8) {
      return "Password must be at least 8 characters long.";
    }
  
    if (password !== confirmPassword) {
      return "Passwords do not match.";
    }
  
    return null; // no error
  }

  validateEmail(email: string): boolean {
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailPattern.test(email);
  
    if (!isValid) {
      this.error_message = "Invalid email format. Please enter a valid email.";
      return false;
    }
  
    //check if unipet
    if (email.toLowerCase().endsWith('@unipet.co')) {
      this.error_message = "Company email detected. Please try logging with Microsoft on the login page. If this error persists, request account creation from Business Transformations's DevOps team.";
      return false;
    }

    this.error_message = "";
    return true;
  }
  
  resetErrorMessage() {
    this.error_message = "";
  }

  checkPasswordRequirements(): void {
    const password = this.createPassword;
    
    this.passwordRequirements.hasUppercase = /[A-Z]/.test(password);
    this.passwordRequirements.hasNumber = /\d/.test(password);
    this.passwordRequirements.hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    this.passwordRequirements.hasMinLength = password.length >= 8;
    
    // Clear error message when typing
    this.error_message = "";
  }

  sendEmailNotification(companyId: number, adminId: number) {
    //creates both in-app and email notification for account creation
    const payload = {
      companyId,
      adminId,
      type: "new_account",
      message: "Account created. Welcome to the UNIPET Vendor Registration Portal.",
      createdAt: new Date(this.dataService.getLocalDate()),
      fieldNames: [] 
    };

    const subject = 'Welcome to the UNIPET Vendor Registration Portal.';

    this.notificationService.sendNotification(payload).pipe(
      switchMap(() => this.notificationService.sendEmailNotification("vendor-account", subject, this.email))
    ).subscribe({
      next: (emailRes) => {
        const w = formatEmailDeliveryUserMessage(emailRes, 'notification');
        if (w) {
          this.showSnackbar(w);
        } else {
          this.showSnackbar('Notification sent.');
        }
      },
      error: err => {
        console.error('Error sending email notification:', err);
        this.showSnackbar(describeHttpError(err));
      }
    });
  }

  private showSnackbar(message: string): void {
    const snackbar = document.getElementById('snackbar');
  
    if (snackbar) {
      const snackbarElement = snackbar as HTMLElement & { timeoutId?: number };
  
      if (snackbarElement.timeoutId) {
        clearTimeout(snackbarElement.timeoutId);
      }
  
      snackbarElement.style.visibility = 'visible';
      snackbarElement.style.opacity = '1';
      snackbarElement.innerText = message;
      snackbarElement.classList.add('show');
  
      snackbarElement.timeoutId = setTimeout(() => {
        snackbarElement.style.visibility = 'hidden';
        snackbarElement.style.opacity = '0';
        snackbarElement.classList.remove('show');
      }, 3000) as unknown as number;
    }
  }


}
